import React from 'react';
import { Form, Spinner } from 'react-bootstrap';
import Popup from 'reactjs-popup';
import { useIsPsAdmin } from '../hooks';
import { useQuoteAwning, useUpdateQuoteAwning } from '../Store/entities/awnings/hooks';
import AwningAction from './NewOptionFields/AwningActions';
import { useQuoteById } from '../Store/entities/quotes/hooks';
import { useCurrentMultiplier } from '../Store/entities/multipliers/hooks';
import Money from '../Parts/Money';
import PowerShadesTypeahead from '../Parts/PowerShadesTypeahead';
import QuoteAwningLockSelect from './QuoteAwningLockSelect';
import TextField from './NewOptionFields/TextField';
import {
	applyMountingDefaultsToParameters,
	getAwningParamValue,
	getAwningPriceTierFromMultiplierName,
	getAwningTierPrice,
	replaceAwningParamValues,
} from './awningConfiguratorUtils';
import type { AwningFabric, AwningProperty } from './awningTypes';
import type { QuoteAccessoryParameters } from '../powershadesApiTypes';
import type { fakeRootType } from '../FakeRootType';

type PSAwningRowProps = {
	quoteId: number;
	lineNumber: number;
	ROOT: fakeRootType;
	unlockQuoteForEditing: () => void;
	awningProperties: AwningProperty[];
	awningFabrics: AwningFabric[];
};

const getPropertyOptions = (properties: AwningProperty[], propertyNumber: number): string[] => {
	const property = properties.find((p) => p.property_number === propertyNumber);
	return property?.options.map((option) => option.value) ?? [];
};

type AwningFabricOption = {
	label: string;
	value: string;
};

const PSAwningTableRow = ({
	quoteId,
	lineNumber,
	ROOT,
	unlockQuoteForEditing,
	awningProperties,
	awningFabrics,
}: PSAwningRowProps) => {
	const isPsAdmin = useIsPsAdmin();
	const qAwning = useQuoteAwning(quoteId, lineNumber);
	const isEditable = useQuoteById(quoteId)?.editable ?? false;
	const multiplier = useCurrentMultiplier(quoteId);
	const updateQuoteAwning = useUpdateQuoteAwning();

	const currencyMultiplier = multiplier.currency_multiplier ?? 1;

	const fabricNumber = getAwningParamValue(qAwning?.parameters, 'fabricNumber');
	const colorName = getAwningParamValue(qAwning?.parameters, 'colorName');

	const fabricOptions = React.useMemo<AwningFabricOption[]>(
		() => awningFabrics.map((fabric) => ({
			label: fabric.color_name,
			value: fabric.ke_fabric_code,
		})),
		[awningFabrics],
	);

	const selectedFabricOption = React.useMemo(() => {
		const fabric = awningFabrics.find((f) => f.ke_fabric_code === fabricNumber);
		if (fabric) {
			return [{ label: fabric.color_name, value: fabric.ke_fabric_code }];
		}
		if (colorName) {
			return [{ label: colorName, value: fabricNumber || '' }];
		}
		return [];
	}, [fabricNumber, colorName, awningFabrics]);

	if (!qAwning) return null;

	const isUnlocked = Boolean(qAwning.unlocked && isEditable);
	const fieldsDisabled = !isEditable || !isUnlocked;

	// A zeroed awning that is still in the pricing queue is pending, not broken -- it only becomes an
	// error once pricing has come back (or come back with a message) and the price is still 0.
	const isPricing = Boolean(qAwning.is_pricing);
	const isErrored = qAwning.msrp === 0 && !isPricing;
	const errored = isErrored ? 'errored' : '';

	const width = getAwningParamValue(qAwning.parameters, 'width');
	const projection = getAwningParamValue(qAwning.parameters, 'projection');
	const frameColor = getAwningParamValue(qAwning.parameters, 'frameColor');
	const mountingOption = getAwningParamValue(qAwning.parameters, 'mountingOption');

	const price = getAwningTierPrice(qAwning, getAwningPriceTierFromMultiplierName(multiplier.name))
		* currencyMultiplier;
	const awningErrorMessage = qAwning.error_message?.trim()
		|| 'Awning pricing failed. Unlock and update configuration to reprice.';

	const saveAwning = (updates: {
		parms?: QuoteAccessoryParameters[];
		room_name?: string;
		shade_name?: string;
	}) => {
		updateQuoteAwning({
			quoteId,
			lineNumber,
			parms: updates.parms ?? qAwning.parameters ?? [],
			room_name: updates.room_name ?? qAwning.room_name ?? '',
			shade_name: updates.shade_name ?? qAwning.shade_name ?? '',
		});
	};

	const saveParameters = (updates: Record<string, { value: string, description?: string }>) => {
		const parms = replaceAwningParamValues(qAwning.parameters ?? [], updates);
		saveAwning({ parms });
	};

	const handleFabricChange = (nextFabricCode: string) => {
		const fabric = awningFabrics.find((f) => f.ke_fabric_code === nextFabricCode);
		if (!fabric) return;

		saveParameters({
			fabricBrand: { value: fabric.fabric_brand, description: 'FABRIC BRAND' },
			fabricNumber: { value: fabric.ke_fabric_code, description: 'FABRIC NUMBER' },
			colorName: { value: fabric.color_name, description: 'Fabric Color' },
		});
	};

	const handleFabricClear = () => {
		saveParameters({
			fabricBrand: { value: '', description: 'FABRIC BRAND' },
			fabricNumber: { value: '', description: 'FABRIC NUMBER' },
			colorName: { value: '', description: 'Fabric Color' },
		});
	};

	const handleMountingChange = (nextMounting: string) => {
		const baseParms = replaceAwningParamValues(qAwning.parameters ?? [], {
			mountingOption: { value: nextMounting, description: 'MOUNTING OPTION' },
		});
		const parms = applyMountingDefaultsToParameters(baseParms);
		saveAwning({ parms });
	};

	const widthOptions = getPropertyOptions(awningProperties, 2);
	const projectionOptions = getPropertyOptions(awningProperties, 3);
	const frameColorOptions = getPropertyOptions(awningProperties, 5);
	const mountingOptions = getPropertyOptions(awningProperties, 6);

	return (
		<tr className={errored} key={lineNumber}>
			<td className="py-4">
				<QuoteAwningLockSelect
					quoteId={quoteId}
					lineNumber={lineNumber}
					unlockQuoteForEditing={unlockQuoteForEditing}
				/>
			</td>
			<td>
				<TextField
					disabled={fieldsDisabled}
					value={qAwning.room_name ?? ''}
					onBlur={(value) => saveAwning({ room_name: value })}
					handleEnter={(value) => saveAwning({ room_name: value })}
				/>
			</td>
			<td>
				<TextField
					disabled={fieldsDisabled}
					value={qAwning.shade_name ?? ''}
					onBlur={(value) => saveAwning({ shade_name: value })}
					handleEnter={(value) => saveAwning({ shade_name: value })}
				/>
			</td>
			<td>
				<PowerShadesTypeahead
					id={`awning-fabric-${lineNumber}`}
					labelKey="label"
					disabled={fieldsDisabled || awningFabrics.length === 0}
					options={fabricOptions}
					selected={selectedFabricOption}
					positionFixed
					placeholder="Select fabric..."
					onChange={(selected) => {
						if (selected.length === 0) {
							handleFabricClear();
							return;
						}
						const option = selected[0] as AwningFabricOption;
						if (option?.value) {
							handleFabricChange(option.value);
						}
					}}
					style={{ minWidth: '100%' }}
				/>
			</td>
			<td>
				<Form.Select
					disabled={fieldsDisabled || widthOptions.length === 0}
					value={width}
					onChange={(e) => saveParameters({ width: { value: e.target.value, description: 'UNIT WIDTH' } })}
				>
					<option value="">Select...</option>
					{widthOptions.map((option) => (
						<option key={option} value={option}>{option}</option>
					))}
				</Form.Select>
			</td>
			<td>
				<Form.Select
					disabled={fieldsDisabled || projectionOptions.length === 0}
					value={projection}
					onChange={(e) => (
						saveParameters({ projection: { value: e.target.value, description: 'PROJECTION' } })
					)}
				>
					<option value="">Select...</option>
					{projectionOptions.map((option) => (
						<option key={option} value={option}>{option}</option>
					))}
				</Form.Select>
			</td>
			<td>
				<Form.Select
					disabled={fieldsDisabled || frameColorOptions.length === 0}
					value={frameColor}
					onChange={(e) => (
						saveParameters({ frameColor: { value: e.target.value, description: 'FRAME COLOR' } })
					)}
				>
					<option value="">Select...</option>
					{frameColorOptions.map((option) => (
						<option key={option} value={option}>{option}</option>
					))}
				</Form.Select>
			</td>
			<td>
				<Form.Select
					disabled={fieldsDisabled || mountingOptions.length === 0}
					value={mountingOption}
					onChange={(e) => handleMountingChange(e.target.value)}
				>
					<option value="">Select...</option>
					{mountingOptions.map((option) => (
						<option key={option} value={option}>{option}</option>
					))}
				</Form.Select>
			</td>
			<td>
				{isPricing ? (
					<Spinner size="sm" animation="border" title="Pricing..." />
				) : isErrored ? (
					<Popup trigger={<span>Error</span>} position="right center" on="hover">
						<div>
							<h6>{awningErrorMessage}</h6>
						</div>
					</Popup>
				) : (
					<span><Money value={price} /></span>
				)}
			</td>
			{isPsAdmin && (
				<td>
					{isPricing ? (
						<Spinner size="sm" animation="border" title="Pricing..." />
					) : (
						<Money value={qAwning.shipping * currencyMultiplier} />
					)}
				</td>
			)}
			<td className="right-most-cell">
				<AwningAction
					quoteId={quoteId}
					lineNumber={lineNumber}
					ROOT={ROOT}
					displayName={
						qAwning.shade_name
							? (qAwning.room_name
								? `${qAwning.shade_name} (${qAwning.room_name})`
								: qAwning.shade_name)
							: (colorName
								? `${qAwning.name || 'Awning'} - ${colorName}`
								: (qAwning.name || 'Awning'))
					}
				/>
			</td>
		</tr>
	);
};

export default PSAwningTableRow;
